import React from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, Button, Card, Grid, Stack, Typography } from '@mui/material'
import { blue, grey } from '@mui/material/colors'
import { useGlobalState } from 'state'
import { SaveButton } from 'components/questions'
import { AppHeader } from 'components/app'
import { QuestionPage } from 'common/types'
import { QuestionPages } from 'common/consts'


export const QuestionFrameContext = React.createContext<number>(0)

type QuestionFrameProps = {
  q: number
  nextPageDisabled?: boolean
  centerButtonDisabled?: boolean
  centerButton?: React.ReactNode
  nextPageAction?: () => void
  nextPageLabel?: string
}

export const QuestionFrame = ({
  q,
  nextPageDisabled = false,
  centerButtonDisabled = false,
  centerButton,
  nextPageAction,
  nextPageLabel = '次へ',
  children
}: React.PropsWithChildren<QuestionFrameProps>) => {
  const state = useGlobalState()
  const navigate = useNavigate()

  const frameElement = React.useRef<HTMLDivElement | null>(null)

  const navPage = (page?: string) => {
    if (page) {
      navigate(page, {replace: false})
    }
  }

  const priorPage = React.useMemo((): string | null => {
    if (q === -1 || q === 99) {
      return null
    }
    const targetQ = [1, 2, 3, 4].includes(q) ? 1 : q
    const targets = QuestionPages.filter((page) => page.q < targetQ && !state.app.excludePages.includes(page.q))
    if (targets.length > 0) {
      return targets[targets.length - 1].to
    } else {
      return '/qlist'
    }
  }, [q, state.app.excludePages])

  const nextPage = React.useMemo((): string => {
    if (q === 99) {
      return ''
    }

    const targetQ = [1, 2, 3, 4].includes(q) ? 4 : q
    const target = QuestionPages.find((page) => page.q > targetQ && !state.app.excludePages.includes(page.q))
    if (target) {
      return target.to
    } else {
      return '/qlist'
    }
  }, [q, state.app.excludePages])

  React.useEffect(() => {
    if (frameElement.current) {
      frameElement.current.scrollIntoView()
    }
  }, [])

  const questionPage: QuestionPage | null = React.useMemo(
    () => {
      return QuestionPages.find((page) => page.q === q) || null
    },
    [q]
  )

  const nextPageClickHandler = () => {
    if (nextPageAction !== undefined) {
      nextPageAction()
      setTimeout(() => {
        navPage(nextPage)
      }, 500)
    } else {
      navPage(nextPage)
    }
  }

  return (
    <QuestionFrameContext.Provider value={q === undefined ? 0 : q}>
      <Box position={'fixed'} right={-6} top={34}>
        <SaveButton />
      </Box>
      <Stack direction={'column'} alignItems="center" sx={{backgroundColor: 'white'}} minHeight={'100dvh'} ref={frameElement}>

        <AppHeader />

        <Card elevation={2} sx={{
          width: '80%',
          minWidth: 460,
          maxWidth: 1020,
          backgroundColor: grey[50],
          px: 5,
          py: 2,
          my: 4
        }}>
          {
            questionPage && (
              <>
                <Typography variant='h5' align='center' fontWeight={'bold'} color={blue[500]} whiteSpace={'pre-wrap'} sx={{textShadow: '1px 1px 1px rgba(0, 0, 0, 0.3)'}} >{`【${questionPage.title}】`}</Typography>
                {
                  questionPage.q >= 5 && (
                    <>
                      <Typography variant='h5' align='center' fontWeight={'bold'} color={blue[500]} whiteSpace={'pre-wrap'} sx={{textShadow: '1px 1px 1px rgba(0, 0, 0, 0.3)'}} >{questionPage.subtitle}</Typography>
                      { questionPage.heading && <Typography variant='h6' align='center' pt={1}>{questionPage.heading}</Typography> }
                      { <Typography variant='h6' align='left' my={3}>{`${questionPage.q}. ${questionPage.content}`}</Typography> }
                    </>
                  )
                }
              </>
            )
          }

          <Stack direction="column" alignItems={'start'} pt={1} pb={4}>
            {children}
          </Stack>

          <Grid container justifyContent="space-between" px={3} sx={{minWidth: 500}}>
            <Box  sx={{width: 120}} >
              { priorPage && <Button variant='contained' sx={{width: 120}} onClick={() => navPage(priorPage)}>戻る</Button> }
            </Box>
            <Box sx={{width: 120}} >
              { !centerButton && <Button variant='contained' sx={{width: 120}} onClick={() => navPage('/qlist')} disabled={centerButtonDisabled}>設問一覧</Button> }
              { centerButton }
            </Box>
            <Box sx={{width: 120}} >
              { nextPage && <Button variant='contained' sx={{width: 120}} onClick={nextPageClickHandler} disabled={nextPageDisabled}>{nextPageLabel}</Button> }
            </Box>
          </Grid>

        </Card>
        <Stack sx={{minHeight: 80, backgroundColor: '#343a40', width: '100%', pt: 2}} flexGrow={1} justifyContent={'start'} alignItems={'center'}>
          <Typography variant='body2' color={grey[300]}>Copyright © CTI Engineering Co., Ltd.</Typography>
        </Stack>
      </Stack>
    </QuestionFrameContext.Provider>
  )
}
